import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiCheckLight,
  PiGlobeLight,
  PiInfoLight,
  PiRulerLight,
  PiTagLight,
  PiWarningCircleLight,
} from 'react-icons/pi';
import GlassCard from '../shared/brand/GlassCard';
import BrandLoader from '../shared/brand/BrandLoader';
import { Field, FieldGrid } from '../shared/brand/Field';
import { Triage } from '../../services/api/marketplaceService';
import { enumLabel } from './marketplaceLabels';
import { UrgencyBadge, countryName } from './marketplaceUi';

/**
 * Read-only triage result. Shared by the client's request view and the
 * lawyer's (redacted) inbox view. The not-legal-advice line leads, on purpose.
 *
 * No advice to the client: the client sees the routing labels only (issue
 * type, jurisdiction). Urgency, scope and the documents to request are an
 * assessment of the matter, for lawyers. The backend already strips them from
 * the client's lead; `audience="client"` keeps them off screen regardless.
 */
const TriageSummary: React.FC<{ triage: Triage; audience: 'client' | 'lawyer' }> = ({ triage, audience }) => {
  const { t } = useTranslation();
  const forLawyer = audience === 'lawyer';

  return (
    // Untitled: it always sits under a "Triage" canvas Section.
    <GlassCard>
      <div className="space-y-4" data-testid="marketplace-triage">
        <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-fg-muted">
          <PiInfoLight className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {/* The backend's disclaimer (same text) in the reader's language. */}
          <span>{t('This is an automated categorisation used only to route your request to suitable lawyers. It is not legal advice and does not create a lawyer-client relationship. Only a lawyer you engage can advise you.')}</span>
        </p>
        {triage.model_meta?.ai_status === 'pending' && (
          <div className="flex items-center gap-1.5 text-[11px] text-fg-muted" data-testid="marketplace-triage-refining">
            <BrandLoader size="inline" />
            <span>{t('Refining with AI…')}</span>
          </div>
        )}
        <FieldGrid columns={2} measure="container">
          <Field icon={<PiTagLight />} label={t('Issue type')} value={enumLabel(t, triage.issue_type)} />
          <Field icon={<PiGlobeLight />} label={forLawyer ? t('Jurisdiction') : t('Country')} value={countryName(t, triage.jurisdiction)} />
          {forLawyer && triage.urgency && (
            <Field icon={<PiWarningCircleLight />} label={t('Urgency')}>
              <UrgencyBadge urgency={triage.urgency} />
            </Field>
          )}
        </FieldGrid>
        {forLawyer && triage.scope_est && (
          <Field icon={<PiRulerLight />} label={t('Estimated scope')}>
            <span className="text-sm text-fg-body">
              {enumLabel(t, triage.scope_est.size)} · {triage.scope_est.summary}
            </span>
          </Field>
        )}
        {forLawyer && triage.prep && triage.prep.length > 0 && (
          <div className="border-t border-hairline-soft pt-3">
            <div className="mb-2 text-[11px] text-fg-muted">{t('Documents to request from the client')}</div>
            <ul className="space-y-1.5">
              {triage.prep.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-fg-body">
                  <PiCheckLight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-fg-muted" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </GlassCard>
  );
};

export default TriageSummary;
